import styled from 'astroturf/react';
import Svg from 'src/components/Svg';
import React from 'react';
import PropTypes from 'prop-types';

import CheckboxA from 'src/components/Checkbox/Checkbox';

const FindingDiv = styled('div')`
  display: flex;
  border-radius: var(--ph-border-radius-sm);
  border: solid 2px #b2b3b2;
  background-color: var(--white);
  outline: #efefef solid 5px;
`;

const LeftColumn = styled('div')`
  flex: 1;
  padding: 0;
  min-width: 0;
  padding-bottom: 5px;
`;

const LeftColumnRows = styled('div')`
  padding: 20px;
`;

const CenterColumn = styled('div')`
  flex: 2;
  padding: 20px;
  border-left: solid 1px #b2b3b2;
  border-right: solid 1px #b2b3b2;
`;

const RightColumn = styled('div')`
  flex: 1;
  padding: 20px;
  min-width: 0;
  position: relative;
`;

const Label = styled('div')`
  overflow: hidden;
  width: 100%;
  text-overflow: ellipsis;
  height: 35px;
  font-size: var(--ph-text-sm);
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 2.69;
  letter-spacing: normal;
  color: #737373;
  padding-left: 5px;

  &.isQuestionLabel {
    overflow: visible;
    overflow-wrap: break-word;
    line-height: unset;
    height: unset;
    margin-bottom: 10px;
  }

  &.longLabel {
    overflow-wrap: break-word;
    height: unset;
  }
`;

const DivRow = styled('div')`
  display: block;
  width: 100%;

  & + & {
    margin-top: 22px;
  }
`;

const RootCauseText = styled('div')`
  font-size: var(--ph-heading-three);
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: var(--black);
  overflow-wrap: break-word;
  overflow: hidden;
  padding-left: 5px;
  text-overflow: ellipsis;
  white-space: break-spaces;

  &.collapsed {
    font-size: var(--ph-text-sm);
    flex: 9;
  }
`;

const SeverityTextDiv = styled('div')`
  display: flex;
  align-items: center;
  padding-left: 5px;
`;

const TextR = styled('div')`
  min-height: 36px;
  font-size: var(--ph-text-sm);
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: var(--black);
  white-space: pre-wrap;
  padding: 0 5px;
  overflow-wrap: break-word;
  word-wrap: break-word;

  &.leftMarginForSeverityIcon {
    margin-left: 5px;
  }

  &.capitalize {
    text-transform: capitalize;
  }

  &.centerAligned {
    display: flex;
    align-items: center;
  }

  &.empty {
    font-style: italic;
    color: #d9d9d9;
  }

  &.expanded {
    font-weight: 500;
    font-size: var(--ph-text-sm);
    line-height: 1.44;
  }

  &.one-line {
    display: block;
    line-height: 38px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &.title {
    font-size: var(--ph-text-sm);
    font-weight: 600;
  }
`;

const Text = props => {
  const { children, ...rest } = props;
  return (
    <TextR {...rest} empty={!children}>
      {!children ? 'N/A' : children}
    </TextR>
  );
};

Text.propTypes = {
  children: PropTypes.string,
};

const SeverityIcon = styled(Svg)`
  height: 15px;
  width: 15px;

  &:hover {
    opacity: 1;
  }

  &.collapsed {
    flex: 1;
  }
`;

const ExpandSwitch = styled('div')`
  position: absolute;
  right: 20px;
  top: 20px;
  width: 26px;
  height: 26px;
  background-color: #f2f2f2;
  border-radius: var(--ph-border-radius-sm);
  cursor: pointer;
  z-index: 100;

  &::before {
    content: '';
    border: solid var(--black);
    border-width: 0 2px 2px 0;
    display: inline-block;
    padding: 3px;
    transform: rotate(-45deg);
    position: absolute;
    left: 8px;
    top: 9px;
  }

  &:hover {
    cursor: pointer;
  }

  &.expanded {
    transform: rotate(90deg);
  }
`;

const CollapsedRootCauseDiv = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Button = styled('span')`
  width: 60px;
  height: 28px;
  border-radius: var(--ph-border-radius-sm);
  padding: 7px 13px;
  color: white;
  background-color: var(--ph-orange-500);

  &.add {
    background-color: #f2f2f2;
    background-image: none;
    color: black;
  }

  & + & {
    margin-left: 5px;
  }

  &.clickable {
    &:hover {
      cursor: pointer;
    }
  }
`;

const NoActionText = styled('div')`
  font-size: var(--ph-text-sm);
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 2.33;
  letter-spacing: normal;
  color: #7a7a80;
  padding-left: 5px;
`;

const ActionsArea = styled('div')`
  outline: 5px solid #efefef;
  border: 2px solid #efefef;
  background: #efefef;
  width: 100%;
  display: flex;
`;

const ActionsDiv = styled('div')`
  outline: 5px solid #efefef;
  background: #efefef;
  margin-top: 8px;
`;

const ActionHintText = styled('div')`
  flex: 4;
`;

const StatusDiv = styled('div')`
  position: relative;
`;

const NotRequiredAlertIcon = styled(Svg)`
  height: 15px;
  width: 15px;
  position: absolute;
  top: 10px;
  left: 51px;
`;

const Checkbox = styled(CheckboxA)`
  margin: 0;
  height: 15px;
  user-select: none;
  left: -11px;
`;

export default {
  FindingDiv,
  LeftColumn,
  CenterColumn,
  RightColumn,
  Label,
  DivRow,
  RootCauseText,
  Text,
  SeverityIcon,
  SeverityTextDiv,
  ExpandSwitch,
  CollapsedRootCauseDiv,
  NoActionText,
  Button,
  ActionsArea,
  ActionsDiv,
  ActionHintText,
  LeftColumnRows,
  StatusDiv,
  NotRequiredAlertIcon,
  Checkbox,
};
